<template>
  <div class="model-testing">
    <v-container>
      <h1 class="text-center mb-4">Model Comparison Testing</h1>
      <p class="text-center text-grey mb-6">Compare responses from 4 different models</p>

      <v-card class="mb-6">
        <v-card-text>
          <v-textarea
            v-model="question"
            label="Enter your question"
            rows="3"
            variant="outlined"
            :disabled="loading"
          ></v-textarea>
          <v-btn
            color="primary"
            @click="compareModels"
            :loading="loading"
            :disabled="!question || loading"
            block
          >
            Compare Models
          </v-btn>
        </v-card-text>
      </v-card>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-4"
      ></v-progress-linear>

      <v-row v-if="results">
        <v-col
          v-for="model in models"
          :key="model"
          cols="12"
          md="6"
        >
          <v-card :class="{'error-card': results.models[model]?.status === 'error'}">
            <v-card-title class="d-flex justify-space-between align-center">
              <span>{{ model }}</span>
              <v-chip
                :color="results.models[model]?.status === 'success' ? 'success' : 'error'"
                size="small"
              >
                {{ results.models[model]?.status }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <div class="response-text">
                <VueMarkdownRender :source="results.models[model]?.response || ''" />
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card v-if="results && results.sources && results.sources.length > 0" class="mt-6">
        <v-card-title>RAG Sources ({{ results.sources.length }})</v-card-title>
        <v-card-text>
          <v-expansion-panels>
            <v-expansion-panel
              v-for="(source, index) in results.sources"
              :key="index"
            >
              <v-expansion-panel-title>
                {{ source.filename }} - {{ source.discipline }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p><strong>URL:</strong> <a :href="source.url" target="_blank">{{ source.url }}</a></p>
                <p><strong>Themes:</strong> {{ source.themes }}</p>
                <p><strong>Modified:</strong> {{ source.modification_date }}</p>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config/config';
import VueMarkdownRender from 'vue-markdown-render';

export default {
  name: 'ModelTestingView',
  components: {
    VueMarkdownRender
  },
  data() {
    return {
      question: '',
      loading: false,
      results: null,
      models: [
        'openai/gpt-oss-120b',
        'Qwen/Qwen3-Coder-30B-A3B-Instruct',
        'mistralai/Ministral-3-8B-Instruct-2512',
        'mistralai/Mistral-Small-3.2-24B-Instruct-2506'
      ]
    };
  },
  methods: {
    async compareModels() {
      if (!this.question) return;

      this.loading = true;
      this.results = null;

      try {
        const response = await axios.post(
          `${config.apiUrl}/compare-models`,
          { question: this.question }
        );
        this.results = response.data;
      } catch (error) {
        console.error('Error comparing models:', error);
        alert('Error comparing models: ' + (error.response?.data?.detail || error.message));
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.model-testing {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.response-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Roboto', sans-serif;
  line-height: 1.6;
}

.response-text :deep(p) {
  margin-bottom: 12px;
}

.response-text :deep(ul),
.response-text :deep(ol) {
  margin-left: 20px;
  margin-bottom: 12px;
}

.response-text :deep(li) {
  margin-bottom: 6px;
}

.response-text :deep(strong) {
  font-weight: 600;
  color: #1976d2;
}

.response-text :deep(code) {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.response-text :deep(pre) {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin-bottom: 12px;
}

.response-text :deep(a) {
  color: #1976d2;
  text-decoration: none;
}

.response-text :deep(a:hover) {
  text-decoration: underline;
}

.response-text :deep(h1),
.response-text :deep(h2),
.response-text :deep(h3) {
  margin-top: 16px;
  margin-bottom: 12px;
  font-weight: 500;
}

.error-card {
  border: 2px solid #ff5252;
}

.v-card {
  margin-bottom: 16px;
}
</style>
